import { useEffect, useRef, useState } from "react";
import { mapsApi } from "../../lib/api";

// ~110 m buckets — only refetch the road route when origin/target moves past a bucket,
// so a smoothly animating driver marker doesn't spam the Routes API.
const r3 = (n) => Math.round(n * 1000) / 1000;
const DEVIATION_KM = 0.07; // force a reroute if the driver strays >70 m off the drawn path

const kmBetween = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

// Min distance (km) from point p to a polyline (sampled at its vertices — good enough for reroute detection).
const minDistToPath = (p, pts) => {
  let min = Infinity;
  for (const q of pts) {
    const d = kmBetween(p, q);
    if (d < min) min = d;
  }
  return min;
};

// Fetches the real Google driving route (road polyline + traffic-aware duration + live-traffic
// segments) and auto-reroutes when the origin drifts off the current path.
export default function useRoadRoute(from, to, active = true) {
  const [route, setRoute] = useState({ points: [], durationMin: null, distanceKm: null, traffic: [] });
  const keyRef = useRef("");
  const doneRef = useRef("");
  const ptsRef = useRef([]);

  useEffect(() => {
    if (!active || !from || !to || !Number.isFinite(from.lat) || !Number.isFinite(to.lat)) return undefined;
    const key = `${r3(from.lat)},${r3(from.lng)}>${r3(to.lat)},${r3(to.lng)}`;
    const offRoute = ptsRef.current.length >= 2 && minDistToPath(from, ptsRef.current) > DEVIATION_KM;
    if (key === keyRef.current && !offRoute) return undefined;
    keyRef.current = key;
    let alive = true;
    mapsApi
      .directions(`${from.lat},${from.lng}`, `${to.lat},${to.lng}`)
      .then((d) => {
        if (alive && d && Array.isArray(d.points) && d.points.length >= 2) {
          doneRef.current = key;
          ptsRef.current = d.points;
          setRoute({ points: d.points, durationMin: d.duration_min, distanceKm: d.distance_km, traffic: d.traffic || [] });
        }
      })
      .catch(() => {});
    // If this run is torn down before it completes (e.g. StrictMode double-invoke), clear the
    // key so the next run refetches instead of skipping on a stale key.
    return () => { alive = false; if (keyRef.current === key && doneRef.current !== key) keyRef.current = ""; };
  }, [active, from?.lat, from?.lng, to?.lat, to?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  return route;
}
